import { useState } from 'react';
import { useGlobalFilterValues } from '../../hooks/useGlobalFilterValues';
import { useUserState } from '../../hooks/context/useUserState';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { requestDashboard } from '../requests/dashboards/requestDashboard';
import { requestReports } from '../requests/reports/requestReports';
import { requestFilterData } from '../requests/filters/requestFilterData';
import { GetReportsDetailResponseBody } from '../types/generated/reports_get.response';
import { Dashboard, Filter, Report } from '../../types/types';
import {
  SET_DASHBOARD,
  SAVE_REPORTS,
  SET_GLOBAL_FILTER_VALUES,
} from '../../constants';
import { getDateString } from '../../utils/filters/getDateString';
import { getDefaultFilterData } from '../../utils/filters/getDefaultFilterData';
import { getRangeForTimespan } from '../../utils/filters/getRangeForTimespan';

const assignSelectFilterValues = (filter, filterData) => {
  const newFilter: any = { ...filter };
  if (filterData[filter.id]) {
    newFilter.availableValues = [...filterData[filter.id].availableValues];
  } else {
    newFilter.availableValues = [];
  }
  return newFilter;
};

export const useDashboard = () => {
  const {
    state: { token, dashboards, reports },
    dispatch,
  } = useUserState();
  const globalFilterValues = useGlobalFilterValues();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const handleApiError = useApiErrorHandling();
  const fetch = async (id: string, forceHeader?: string) => {
    try {
      const dashboard = await requestDashboard(id, token, forceHeader);
      if (dashboard) {
        const selectFiltersIds: string[] = [];
        if (dashboard.filters) {
          dashboard.filters.forEach((filter: any) => {
            // Get select/multiselect global filters IDs for the filterData request
            if (['SELECT', 'MULTISELECT'].includes(filter.type)) {
              selectFiltersIds.push(filter.id);
            }
          });
        }
        let newReports: GetReportsDetailResponseBody = [];
        if (dashboard.reports?.length) {
          const foundDashboard = dashboards.find((d) => d.id === id);
          const reportsToFetch = dashboard.reports?.filter((report) => {
            if (!foundDashboard || !foundDashboard.reports) {
              return true;
            }
            const foundDashboardReport = foundDashboard.reports.find(
              (r) => r.id === report.id
            );
            const foundReport = reports.find((r) => r.id === report.id);

            if (
              !foundDashboardReport ||
              !foundReport ||
              foundDashboardReport.checksum !== report.checksum
            ) {
              return true;
            }
            return false;
          });
          const reportIdsToFetch = reportsToFetch.map(({ id }) => id);
          // const reportIds = ['revenuedailywow', 'openedbills'];
          if (reportIdsToFetch.length) {
            newReports = await requestReports(
              reportIdsToFetch.toString(),
              token
            );
          }
        }
        const allReports = [...reports];
        // Merge new reports from the /reports GET request with all other reports from local storage
        newReports.forEach((newReport) => {
          const index = allReports.findIndex(({ id }) => id === newReport.id);
          if (index === -1) {
            allReports.push(newReport as Report);
          } else {
            allReports[index] = newReport as Report;
          }
        });
        // Get select/multiselect report filters IDs for the filterData request
        allReports.forEach((report) => {
          if (report.filters) {
            report.filters.forEach((filter) => {
              if (['SELECT', 'MULTISELECT'].includes(filter.type)) {
                selectFiltersIds.push(filter.id);
              }
            });
          }
        });
        const filterData = await requestFilterData(
          selectFiltersIds.toString(),
          token
        );
        // Assign availableValues from filterData request to global filters
        if (dashboard.filters) {
          const filtersWithAvailableValues: Filter[] = dashboard.filters
            .map((filter) => assignSelectFilterValues(filter, filterData))
            .filter(
              (filter) =>
                !(
                  ['SELECT', 'MULTISELECT'].includes(filter.type) &&
                  !filter.availableValues?.length
                )
            );
          dashboard.filters = filtersWithAvailableValues;

          const newGlobalFilterValues = [...globalFilterValues];
          dashboard.filters.forEach((filter: any) => {
            const index = newGlobalFilterValues.findIndex(
              ({ id }) => id === filter.id
            );
            if (index === -1) {
              const defaultFilterData = getDefaultFilterData(filter);
              if (defaultFilterData) {
                newGlobalFilterValues.push(defaultFilterData);
              }
            } else if (
              ['DATE', 'DATERANGE'].includes(filter.type) &&
              newGlobalFilterValues[index].namedRange
            ) {
              const range = getRangeForTimespan(
                newGlobalFilterValues[index].namedRange
              );
              let values;
              if (filter.type === 'DATERANGE' && range.from && range.to) {
                values = [getDateString(range.from), getDateString(range.to)];
              } else if (filter.type === 'DATE' && range.from) {
                values = [getDateString(range.from)];
              }
              if (values) {
                newGlobalFilterValues[index].values = values;
              }
            } else if (
              ['SELECT', 'MULTISELECT'].includes(filter.type) &&
              newGlobalFilterValues[index].values
            ) {
              // Remove values that are no longer in availableValues
              const globalValues = newGlobalFilterValues[index].values as any;
              const values = globalValues.filter((value) => {
                const foundAvailableValue = filter.availableValues.find(
                  (availableValue) => availableValue.key === value
                );
                return !!foundAvailableValue;
              });
              newGlobalFilterValues[index].values = values;
            }
          });
          const filteredGlobalFilterValues = newGlobalFilterValues.filter(
            ({ id }) => {
              const filter = dashboard.filters?.find(
                (filter) => filter.id === id
              );
              return !!filter;
            }
          );
          dispatch({
            type: SET_GLOBAL_FILTER_VALUES,
            filterData: filteredGlobalFilterValues,
          });
        }
        dispatch({
          type: SET_DASHBOARD,
          id,
          data: dashboard as Dashboard,
        });

        // Assign availableValues from filterData request to report filters and save reports to local storage
        if (allReports.length) {
          const reportsWithAvailableValues = allReports.map((report) => {
            if (report.filters) {
              const filtersWithAvailableValues: any[] = report.filters.map(
                (filter) => assignSelectFilterValues(filter, filterData)
              );
              report.filters = filtersWithAvailableValues;
            }
            return report;
          });
          dispatch({
            type: SAVE_REPORTS,
            payload: reportsWithAvailableValues,
          });
        }
      }
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, false, (forceHeader) => fetch(id, forceHeader));
    } finally {
      setIsLoading(false);
    }
  };
  return {
    isLoading,
    error,
    fetch,
  };
};
